import apiBaseState from "~/constants/apiBaseState";
import Vueset from "~/utils/Vueset";

const getState = () => {
    return {
        // For GET questions operation.
        contentAnalysisQuestionState: apiBaseState(),
        // For GET answers operation.
        contentAnalysisAnswerState: apiBaseState(),
        // For CREATE question operation.
        contentAnalysisCreateQuestionState: apiBaseState(),
        // For UPDATE question operation.
        contentAnalysisUpdateQuestionState: apiBaseState(),
        // Tracks deletion per question (an object keyed by questionId).
        contentAnalysisDeleteQuestionState: {},
        // Tracks "regenerate" loading state per question (calls /answers/regenerate endpoint for all answers)
        contentAnalysisReGenerateAnswerState: {},
        // Tracks "refresh" (GET) loading state per question.
        contentAnalysisRefreshAnswerState: {},
        // Tracks "regenerate single answer" loading state per answer.
        contentAnalysisRegenerateSingleAnswerState: {},
        contentAnalysis: null,
        isProcessing: null,
        isProcessingData: null,
    };
};

export const state = () => getState();

export const mutations = {
    RESET_STORE(state) {
        const s = getState();
        Object.keys(s).forEach((key) => {
            state[key] = s[key];
        });
    },
    SET_IS_PROCESSING(state, payload) {
        state.isProcessing = payload;
    },
    SET_IS_PROCESSING_DATA(state, payload) {
        state.isProcessingData = payload;
    },
    SET_CONTENT_ANALYSIS_QUESTION(state, payload) {
        state.contentAnalysisQuestionState = payload;
    },
    SET_CONTENT_ANALYSIS_ANSWER(state, payload) {
        state.contentAnalysisAnswerState = payload;
    },
    SET_CONTENT_ANALYSIS_CREATE_QUESTION_STATE(state, payload) {
        state.contentAnalysisCreateQuestionState = payload;
    },
    SET_CONTENT_ANALYSIS_UPDATE_QUESTION_STATE(state, payload) {
        state.contentAnalysisUpdateQuestionState = payload;
    },
    SET_CONTENT_ANALYSIS_DELETE_QUESTION_STATE(state, { questionId, loading }) {
        Vueset(state.contentAnalysisDeleteQuestionState, questionId, loading);
    },
    // Mutation to track the GET refresh loading state per question.
    SET_CONTENT_ANALYSIS_REFRESH_ANSWER_STATE(state, { questionId, loading }) {
        Vueset(state.contentAnalysisRefreshAnswerState, questionId, loading);
    },
    // Mutation to track loading state for regenerating all answers for a question.
    SET_CONTENT_ANALYSIS_REGENERATE_ANSWER_STATE(state, { questionId, loading }) {
        Vueset(state.contentAnalysisReGenerateAnswerState, questionId, loading);
    },
    // NEW: Mutation to track loading state for regenerating a single answer.
    SET_CONTENT_ANALYSIS_REGENERATE_SINGLE_ANSWER_STATE(state, { answerId, loading }) {
        Vueset(state.contentAnalysisRegenerateSingleAnswerState, answerId, loading);
    },
    // Replaces the entire answers array for a particular question.
    SET_ANSWERS_FOR_CONTENT_ANALYSIS_QUESTION(state, { questionId, answers }) {
        if (state.contentAnalysis && Array.isArray(state.contentAnalysis)) {
            const question = state.contentAnalysis.find((q) => q.id === questionId);
            if (question) {
                Vueset(question, "answers", answers);
            }
        }
    },
    SET_CONTENT_ANALYSIS(state, payload) {
        state.contentAnalysis = payload;
    },
    CLEAR_ANSWER_TO_CONTENT_ANALYSIS_QUESTION(state, payload) {
        const { questionId } = payload;
        if (state.contentAnalysis && Array.isArray(state.contentAnalysis)) {
            const question = state.contentAnalysis.find((q) => q.id === questionId);
            if (question) {
                if (!question.answers) {
                    Vueset(question, "answers", []);
                }
            }
        }
    },
    ADD_ANSWER_TO_CONTENT_ANALYSIS_QUESTION(state, payload) {
        const { response, questionId } = payload;
        if (state.contentAnalysis && Array.isArray(state.contentAnalysis)) {
            const question = state.contentAnalysis.find((q) => q.id === questionId);
            if (question) {
                if (!question.answers) {
                    Vueset(question, "answers", []);
                }
                const addOrReplaceAnswer = (newAnswer) => {
                    const idx = question.answers.findIndex((ans) => ans.id === newAnswer.id);
                    if (idx !== -1) {
                        Vueset(question.answers, idx, newAnswer);
                    } else {
                        question.answers.push(newAnswer);
                    }
                };
                if (Array.isArray(response)) {
                    response.forEach((newAnswer) => addOrReplaceAnswer(newAnswer));
                } else {
                    addOrReplaceAnswer(response);
                }
            }
        }
    },
    ADD_QUESTION_TO_CONTENT_ANALYSIS(state, payload) {
        if (!state.contentAnalysis) {
            Vueset(state, "contentAnalysis", []);
        }
        state.contentAnalysis.push(payload);
    },
    UPDATE_QUESTION_IN_CONTENT_ANALYSIS(state, updatedQuestion) {
        if (state.contentAnalysis && Array.isArray(state.contentAnalysis)) {
            const index = state.contentAnalysis.findIndex((q) => q.id === updatedQuestion.id);
            if (index !== -1) {
                Vueset(state.contentAnalysis, index, updatedQuestion);
            }
        }
    },
    DELETE_QUESTION_FROM_CONTENT_ANALYSIS(state, questionId) {
        if (state.contentAnalysis && Array.isArray(state.contentAnalysis)) {
            const index = state.contentAnalysis.findIndex((q) => q.id === questionId);
            if (index !== -1) {
                state.contentAnalysis.splice(index, 1);
            }
        }
    },
};

export const actions = {
    async GET_CONTENT_ANALYSIS_QUESTION(context, data) {
        const { accountSlug, projectSlug, taskSlug } = data;
        const url = `/accounts/${accountSlug}/${projectSlug}/${taskSlug}/content-analysis/questions`;
        const response = await this.$whyApi.get({
            url,
            mutation: "contentAnalysis/SET_CONTENT_ANALYSIS_QUESTION",
            errorMessage: "Error getting content analysis questions.",
            notify: this.$notify,
        });

        const isProcessing = response.data && !Array.isArray(response.data);

        if(isProcessing){
            context.commit("SET_IS_PROCESSING", true);
            context.commit("SET_IS_PROCESSING_DATA", response.data);
        }else{
            context.commit("SET_IS_PROCESSING", false);
            context.commit("SET_CONTENT_ANALYSIS", response.data);
        }

        //remove this bit for now....
        // if (response.data && Array.isArray(response.data)) {
        //     // Automatically load answers for each question.
        //     response.data.forEach((question) => {
        //         context.dispatch("GET_CONTENT_ANALYSIS_ANSWER", {
        //             accountSlug,
        //             projectSlug,
        //             taskSlug,
        //             questionId: question.id,
        //         });
        //     });
        // }
        return response;
    },
    async GET_CONTENT_ANALYSIS_ANSWER(context, data) {
        // context.commit("CLEAR_ANSWER_TO_CONTENT_ANALYSIS_QUESTION", {
        //     questionId,
        // });
        const { accountSlug, projectSlug, taskSlug, questionId } = data;
        const url = `/accounts/${accountSlug}/${projectSlug}/${taskSlug}/content-analysis/questions/${questionId}/answers`;
        // Set the GET refresh loading state for this question.
        context.commit("SET_CONTENT_ANALYSIS_REFRESH_ANSWER_STATE", { questionId, loading: true });
        try {
            const response = await this.$whyApi.get({
                url,
                mutation: "contentAnalysis/SET_CONTENT_ANALYSIS_ANSWER",
                errorMessage: "Error getting content analysis answers.",
                notify: this.$notify,
            });
            // context.commit("ADD_ANSWER_TO_CONTENT_ANALYSIS_QUESTION", {
            //     response: response.data,
            //     questionId,
            // });
            return response;
        } catch (error) {
            throw error;
        } finally {
            // Clear the refresh loading state.
            context.commit("SET_CONTENT_ANALYSIS_REFRESH_ANSWER_STATE", { questionId, loading: false });
        }
    },
    async CREATE_CONTENT_ANALYSIS_QUESTION(context, data) {
        const { accountSlug, projectSlug, taskSlug, payload } = data;
        const url = `/accounts/${accountSlug}/${projectSlug}/${taskSlug}/content-analysis/questions`;
        const response = await this.$whyApi.post({
            url,
            data: payload,
            mutation: "contentAnalysis/SET_CONTENT_ANALYSIS_CREATE_QUESTION_STATE",
            errorMessage: "Error creating content analysis question.",
            notify: this.$notify,
        });
        context.commit("ADD_QUESTION_TO_CONTENT_ANALYSIS", response.data);
        return response;
    },
    async UPDATE_CONTENT_ANALYSIS_QUESTION(context, data) {
        const { accountSlug, projectSlug, taskSlug, questionId, payload } = data;
        const url = `/accounts/${accountSlug}/${projectSlug}/${taskSlug}/content-analysis/questions/${questionId}`;
        const response = await this.$whyApi.put({
            url,
            data: payload,
            mutation: "contentAnalysis/SET_CONTENT_ANALYSIS_UPDATE_QUESTION_STATE",
            errorMessage: "Error updating content analysis question.",
            notify: this.$notify,
        });
        context.commit("UPDATE_QUESTION_IN_CONTENT_ANALYSIS", response.data);
        return response;
    },
    async DELETE_CONTENT_ANALYSIS_QUESTION(context, data) {
        const { accountSlug, projectSlug, taskSlug, questionId } = data;
        const url = `/accounts/${accountSlug}/${projectSlug}/${taskSlug}/content-analysis/questions/${questionId}`;
        context.commit("SET_CONTENT_ANALYSIS_DELETE_QUESTION_STATE", { questionId, loading: true });
        try {
            const response = await this.$whyApi.delete({
                url,
                mutation: "",
                errorMessage: "Error deleting content analysis question.",
                notify: this.$notify,
            });
            context.commit("DELETE_QUESTION_FROM_CONTENT_ANALYSIS", questionId);
            return response;
        } catch (error) {
            throw error;
        } finally {
            context.commit("SET_CONTENT_ANALYSIS_DELETE_QUESTION_STATE", { questionId, loading: false });
        }
    },
    async REGENERATE_CONTENT_ANALYSIS_ANSWER(context, data) {
        const { accountSlug, projectSlug, taskSlug, questionId, apiQuery } = data;
        const url = `/accounts/${accountSlug}/${projectSlug}/${taskSlug}/content-analysis/questions/${questionId}/answers/regenerate`;
        // Mark the regenerate loading state for all answers in this question.
        context.commit("SET_CONTENT_ANALYSIS_REGENERATE_ANSWER_STATE", { questionId, loading: true });
        try {
            const response = await this.$whyApi.post({
                url,
                params: apiQuery,
                mutation: "",
                errorMessage: "Error regenerating content analysis answers.",
                notify: this.$notify,
            });
            return response;
        } catch (error) {
            throw error;
        } finally {
            context.commit("SET_CONTENT_ANALYSIS_REGENERATE_ANSWER_STATE", { questionId, loading: false });
        }
    },
    async REGENERATE_SINGLE_ANSWER(context, data) {
        const { accountSlug, projectSlug, taskSlug, questionId, answerId } = data;
        const url = `/accounts/${accountSlug}/${projectSlug}/${taskSlug}/content-analysis/questions/${questionId}/answers/${answerId}/regenerate`;
        // Mark the regenerate loading state for this specific answer.
        context.commit("SET_CONTENT_ANALYSIS_REGENERATE_SINGLE_ANSWER_STATE", { answerId, loading: true });
        try {
            const response = await this.$whyApi.post({
                url,
                mutation: "",
                errorMessage: "Error regenerating the answer.",
                notify: this.$notify,
            });
            return response;
        } catch (error) {
            throw error;
        } finally {
            context.commit("SET_CONTENT_ANALYSIS_REGENERATE_SINGLE_ANSWER_STATE", { answerId, loading: false });
        }
    },
    // NEW: Action to get a single answer by its ID.
    async GET_SINGLE_CONTENT_ANALYSIS_ANSWER(context, data) {
        const { accountSlug, projectSlug, taskSlug, questionId, answerId } = data;
        const url = `/accounts/${accountSlug}/${projectSlug}/${taskSlug}/content-analysis/questions/${questionId}/answers/${answerId}`;
        try {
            const response = await this.$whyApi.get({
                url,
                mutation: "",
                errorMessage: "Error getting the content analysis answer.",
                notify: this.$notify,
            });
            // Update or add the single answer into the question's answers array.
            return response;
        } catch (error) {
            throw error;
        }
    },
};
